import React, { useState, useRef, useEffect } from "react";

const getTimestamp = () => new Date().toISOString();

const hydrationCues = [
  "🪥 After brushing your teeth",
  "💊 When taking your supplements",
  "🧘 After your meditation or journaling",
  "🍽 Before lunch or dinner",
  "⏰ Set a phone timer or recurring reminder",
  "📋 Post-it notes (around your house bathroom, kitchen, bedroom, etc.)",
  "🛏 Right after getting out of bed",
  "🚶 Before or after going on a walk",
  "🐶 After feeding your pet or taking them out",
  "📧 Before starting your workday / checking email",
  "📺 Before turning on Netflix / TV",
  "🍵 When craving a snack or coffee",
  "💻 After a Zoom call or meeting",
  "🧹 After doing a household chore (laundry, dishes, vacuuming, etc.)",
  "[Manually Enter]",
];

const Hydration = ({
  logEntry,
  onChange,
  isVisible,
  dayNumber,
  fullLog,
  streak,
}) => {
  if (!isVisible) {
    return null;
  }

  const [showCueDropdown, setShowCueDropdown] = useState(false);
  const [customCue, setCustomCue] = useState("");
  const [addingCustomCue, setAddingCustomCue] = useState(false);
  const dropdownRef = useRef(null);

  // Get cues from previous day (not day 1)
  const getPreviousDayCues = () => {
    if (dayNumber <= 1) return [];
    const previousDayKey = `Day ${dayNumber - 1}`;
    const previousDayLog = fullLog[previousDayKey];
    return previousDayLog && previousDayLog.cues ? previousDayLog.cues : [];
  };

  // Determine if user has ever modified cues for this day
  const hasUserModifiedCues = logEntry.cues !== undefined;

  // Get the effective cues to display
  // If user has modified cues, use their choice (even if empty)
  // If user hasn't modified cues, inherit from previous day
  const effectiveCues = hasUserModifiedCues
    ? logEntry.cues || []
    : getPreviousDayCues();

  console.log("effectiveCues calculation:", {
    hasUserModifiedCues,
    logEntryCues: logEntry.cues,
    previousDayCues: getPreviousDayCues(),
    effectiveCues,
  });

  useEffect(() => {
    if (!showCueDropdown) return;
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCueDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCueDropdown]);

  const handleChange = (field, value) => {
    onChange({
      ...logEntry,
      [field]: value,
      Timestamp: logEntry.Timestamp || getTimestamp(),
    });
  };

  const handleCueToggle = (cueIndex) => {
    const completedCues = logEntry.completedCues || [];
    const newCompletedCues = completedCues.includes(cueIndex)
      ? completedCues.filter((index) => index !== cueIndex)
      : [...completedCues, cueIndex];

    handleChange("completedCues", newCompletedCues);
  };

  const handleAddCue = (cue) => {
    if (cue === "[Manually Enter]") {
      setAddingCustomCue(true);
      setShowCueDropdown(false);
      return;
    }

    // Get current cues (either user's modified cues or inherited)
    let currentCues = effectiveCues;

    console.log("handleAddCue:", {
      cue,
      currentCues,
      dayNumber,
      hasUserModifiedCues,
      alreadySelected: currentCues.includes(cue),
      canAdd: currentCues.length < 3,
    });

    if (!currentCues.includes(cue) && currentCues.length < 3) {
      const newCues = [...currentCues, cue];
      console.log("Adding cue, new cues:", newCues);
      handleChange("cues", newCues);
    } else if (currentCues.includes(cue)) {
      // If cue is already selected, remove it (toggle behavior)
      const newCues = currentCues.filter((c) => c !== cue);
      console.log("Removing cue, new cues:", newCues);
      handleChange("cues", newCues);
    }
    setShowCueDropdown(false);
  };

  const handleAddCustomCue = () => {
    // Get current cues (either user's modified cues or inherited)
    let currentCues = effectiveCues;

    if (customCue.trim() && currentCues.length < 3) {
      const newCues = [...currentCues, customCue.trim()];
      handleChange("cues", newCues);
      setCustomCue("");
      setAddingCustomCue(false);
    }
  };

  const handleRemoveCue = (cueToRemove) => {
    console.log("Removing cue:", cueToRemove);

    // Get current cues (either user's modified cues or inherited)
    let currentCues = effectiveCues;

    // Remove the cue
    const newCues = currentCues.filter((cue) => cue !== cueToRemove);
    console.log("New cues array:", newCues);

    // Update the cues
    handleChange("cues", newCues);
  };

  const allCuesComplete = (logEntry.completedCues || []).length === 3;

  return (
    <div className="tw-px-4 tw-mb-2">
      {/* Behavioral Note */}
      <div className="tw-mb-6 tw-p-4 tw-bg-blue-50 dark:tw-bg-blue-900/20 tw-rounded-lg tw-border-l-4 tw-border-blue-400">
        <p className="tw-text-sm tw-text-blue-800 dark:tw-text-blue-200">
          💧 <strong>Hydration Tip:</strong> At each cue, aim to drink 16 ounces
          of water with electrolytes or a pinch of mineral salt.
        </p>
      </div>

      {/* Cue Selection - Same UI for all days */}
      <div className="tw-mb-6">
        <label className="tw-block tw-font-medium tw-mb-2">
          Choose your 3 hydration cues for this challenge:
        </label>
        <div className="tw-relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setShowCueDropdown(!showCueDropdown)}
            className="tw-w-full tw-border tw-rounded-lg tw-p-4 tw-text-left tw-bg-white dark:tw-bg-black"
          >
            {effectiveCues.length === 0
              ? "Select your hydration cues..."
              : `${effectiveCues.length}/3 cues selected`}
          </button>
          {showCueDropdown && (
            <div className="tw-absolute tw-z-10 tw-w-full tw-bg-white dark:tw-bg-black tw-border tw-border-gray-300 tw-rounded-xl tw-overflow-hidden tw-max-h-80 tw-overflow-y-auto">
              {hydrationCues.map((cue) => {
                const isSelected = effectiveCues.includes(cue);
                return (
                  <div
                    key={cue}
                    className={`tw-px-4 tw-py-3 tw-cursor-pointer tw-border-b tw-border-gray-100 dark:tw-border-gray-700 ${
                      isSelected
                        ? "tw-bg-blue-100 dark:tw-bg-blue-900 tw-text-blue-800 dark:tw-text-blue-200"
                        : "hover:tw-bg-gray-100 dark:hover:tw-bg-gray-700"
                    }`}
                    onClick={() => handleAddCue(cue)}
                  >
                    <div className="tw-flex tw-items-center tw-justify-between">
                      <span>{cue}</span>
                      {isSelected && (
                        <span className="tw-text-blue-600 dark:tw-text-blue-400">
                          ✓
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {addingCustomCue && (
          <div className="tw-flex tw-gap-2 tw-mt-2">
            <input
              type="text"
              className="tw-border tw-rounded-lg tw-p-4 tw-w-full"
              placeholder="Enter your custom hydration cue"
              value={customCue}
              onChange={(e) => setCustomCue(e.target.value)}
            />
            <button
              type="button"
              onClick={handleAddCustomCue}
              className={`tw-px-5 tw-rounded-lg tw-min-h-[50px] tw-text-sm ${
                customCue.trim()
                  ? "tw-bg-green-500 tw-text-white hover:tw-bg-green-600"
                  : "tw-bg-gray-300 tw-text-gray-500 tw-cursor-not-allowed"
              }`}
              disabled={!customCue.trim()}
            >
              Add
            </button>
          </div>
        )}

        {effectiveCues.length > 0 && (
          <div className="tw-mt-3">
            <div className="tw-flex tw-flex-wrap tw-gap-2">
              {effectiveCues.map((cue, index) => (
                <div
                  key={index}
                  className="tw-flex tw-items-center tw-bg-blue-100 dark:tw-bg-blue-900 tw-text-blue-800 dark:tw-text-blue-200 tw-text-sm tw-px-3 tw-py-2 tw-rounded-full"
                >
                  {cue}
                  <button
                    type="button"
                    onClick={() => handleRemoveCue(cue)}
                    className="tw-ml-2 tw-text-blue-600 dark:tw-text-blue-300 hover:tw-text-blue-800 dark:hover:tw-text-blue-100"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Hydration Tracking Table */}
      {effectiveCues.length > 0 && (
        <div className="tw-mb-6">
          <div className="tw-overflow-x-auto">
            <table className="tw-w-full tw-border-collapse tw-border tw-border-gray-300 dark:tw-border-gray-600">
              <thead>
                <tr className="tw-bg-gray-50 dark:tw-bg-gray-800">
                  <th className="tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-p-3 tw-text-left tw-font-medium">
                    Date
                  </th>
                  {effectiveCues.map((cue, index) => (
                    <th
                      key={index}
                      className="tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-p-3 tw-text-left tw-font-medium tw-max-w-[200px]"
                    >
                      <div className="tw-text-sm tw-break-words">{cue}</div>
                    </th>
                  ))}
                  <th className="tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-p-3 tw-text-center tw-font-medium">
                    ✅ All 3 Complete?
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-p-3 tw-font-medium">
                    Day {dayNumber}
                  </td>
                  {effectiveCues.map((cue, index) => (
                    <td
                      key={index}
                      className="tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-p-3 tw-text-center"
                    >
                      <input
                        type="checkbox"
                        checked={(logEntry.completedCues || []).includes(index)}
                        onChange={() => handleCueToggle(index)}
                        className="tw-w-5 tw-h-5 tw-text-blue-600 tw-bg-gray-100 tw-border-gray-300 tw-rounded focus:tw-ring-blue-500 dark:focus:tw-ring-blue-600 dark:tw-ring-offset-gray-800 focus:tw-ring-2 dark:tw-bg-gray-700 dark:tw-border-gray-600"
                      />
                    </td>
                  ))}
                  <td className="tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-p-3 tw-text-center">
                    {allCuesComplete ? (
                      <span className="tw-text-green-600 dark:tw-text-green-400 tw-font-bold">
                        ✅
                      </span>
                    ) : (
                      <span className="tw-text-gray-400">⭕</span>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Progress Summary */}
      {effectiveCues.length > 0 && (
        <div className="tw-mb-6">
          <div className="tw-bg-gray-50 dark:tw-bg-gray-800 tw-p-4 tw-rounded-lg">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
              <h3 className="tw-font-medium">Today's Progress</h3>
              {streak > 0 && (
                <div className="tw-bg-blue-100 dark:tw-bg-blue-900 tw-text-blue-800 dark:tw-text-blue-200 tw-px-3 tw-py-1 tw-rounded-full tw-text-sm tw-font-medium">
                  🔥 {streak} day{streak > 1 ? "s" : ""} streak!
                </div>
              )}
            </div>
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
              <div className="tw-flex-1 tw-bg-gray-200 dark:tw-bg-gray-700 tw-rounded-full tw-h-2 tw-overflow-hidden">
                <div
                  className="tw-bg-blue-600 tw-h-2 tw-rounded-full tw-transition-all tw-duration-300"
                  style={{
                    width: `${
                      ((logEntry.completedCues || []).length / 3) * 100
                    }%`,
                  }}
                ></div>
              </div>
              <span className="tw-text-sm tw-font-medium">
                {(logEntry.completedCues || []).length}/3 completed
              </span>
            </div>
            {allCuesComplete && (
              <p className="tw-text-green-600 dark:tw-text-green-400 tw-text-sm tw-font-medium">
                🎉 Great job! You've completed all 3 hydration cues today!
              </p>
            )}
          </div>
        </div>
      )}

      {/* Notes */}
      <div className="tw-mb-6">
        <label className="tw-block tw-font-medium tw-mb-2">
          Notes (optional)
        </label>
        <textarea
          value={logEntry.notes || ""}
          onChange={(e) => handleChange("notes", e.target.value)}
          className="tw-w-full tw-border tw-rounded-lg tw-p-4 dark:tw-bg-black"
          rows={3}
          placeholder="How are you feeling? Any challenges or wins with hydration today?"
        />
      </div>
    </div>
  );
};

export default Hydration;
